import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, InviteUserModal, InviteUserModalTranslations, InviteUserPayload } from '@cdaas/reusable-components-daas';
import { InvoicingAccountOptionResponseContents, OrganizationResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { assetAdminUserClient } from '../../../api-client/asset-admin-client';
import axios from 'axios';

interface InviteUserProps {
  organization: OrganizationResponse;
  invoicingAccounts: InvoicingAccountOptionResponseContents[];
  closeModal: (updated: boolean) => void;
}

const InviteUser: FunctionComponent<InviteUserProps> = ({ closeModal, invoicingAccounts, organization }) => {
  const { t } = useTranslation('organizationForm');
  const { t: tGlobal } = useTranslation('global');

  const translations: InviteUserModalTranslations = {
    assignToUserGroup: t('inviteUserModal.assignToUserGroup'),
    emailInputLabel: t('inviteUserModal.email'),
    phoneNumberInputLabel: t('inviteUserModal.phoneNumber'),
    employeeIdInputLabel: t('inviteUserModal.employeeId'),
    closeButton: t('inviteUserModal.cancelButton'),
    saveButton: t('inviteUserModal.saveButton'),
    modalTitle: t('inviteUserModal.inviteUser'),
    fieldRequired: t('inviteUserModal.fieldRequired'),
    required: t('inviteUserModal.required'),
    invalidEmail: t('inviteUserModal.invalidEmail'),
    invalidPhoneNumber: t('inviteUserModal.invalidPhoneNumber'),
    saveApiErrorMessage: t('inviteUserModal.saveErrorMessage'),
    saveApiErrorTitle: t('inviteUserModal.saveErrorTitle'),
    purchaseLimitsTitle: t('inviteUserModal.purchaseLimitsTitle'),
    purchaseLimitsDescription: t('inviteUserModal.purchaseLimitsDescription'),
    limitVisibilityTitle: t('inviteUserModal.limitVisibilityTitle'),
    overrideVisibilityCheckboxLabel: t('inviteUserModal.overrideVisibilityCheckboxLabel'),
    maxPricePerProductInputLabel: t('inviteUserModal.maxPricePerProductInputLabel', { currency: organization.locale.currency.toUpperCase() }),
    noVisibilityRestrictionMessage: t('inviteUserModal.noVisibilityRestrictionMessage'),
    restrictedVisibilityMessage: t('inviteUserModal.restrictedVisibilityMessage'),
    restrictedVisibilityMessageEnding: t('inviteUserModal.restrictedVisibilityMessageEnding'),
    month: t('inviteUserModal.monthShort'),
    subscriptionLimitsTitle: t('inviteUserModal.subscriptionLimitsTitle'),
    limitSubscriptionCheckBoxLabel: t('inviteUserModal.limitSubscriptionCheckBoxLabel'),
    enabledSingleDevicePolicyMessage: t('inviteUserModal.enabledSingleDevicePolicyMessage'),
    disabledSingleDevicePolicyMessage: t('inviteUserModal.disabledSingleDevicePolicyMessage'),
    procurementLimitTypeError: t('inviteUserModal.procurementLimitTypeError'),
    invoicingAccount: {
      title: t('inviteUserModal.invoicingAccount.title'),
      dropdownLabel: t('inviteUserModal.invoicingAccount.dropdownLabel'),
      clearSelection: t('inviteUserModal.invoicingAccount.clearSelection'),
      userGroupNotSelected: t('inviteUserModal.invoicingAccount.userGroupNotSelected'),
      noOptionsFoundInfo: t('inviteUserModal.invoicingAccount.noOptionsFoundInfo'),
    },
    showExpanderButton: t('inviteUserModal.showExpanderButton'),
    hideExpanderButton: t('inviteUserModal.hideExpanderButton'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Sweden: tGlobal('countries.Sweden'),
      Norway: tGlobal('countries.Norway'),
      Denmark: tGlobal('countries.Denmark'),
      UnitedKingdom: tGlobal('countries.UnitedKingdom'),
    },
  };

  const onUserSaveHandler = async (payload: InviteUserPayload): Promise<boolean> => {
    try {
      await assetAdminUserClient.adminUserCreate(organization.id!, payload.selectedUserGroupId!, payload.user);
      closeModal(true);
      return true;
    } catch (err) {
      translations.saveApiErrorMessage = (axios.isAxiosError(err) && err.response?.data?.message) || t('inviteUserModal.saveErrorMessage');
      return false;
    }
  };

  return (
    <InviteUserModal
      countries={COUNTRIES}
      organization={organization}
      invoicingAccounts={invoicingAccounts}
      onUserSave={onUserSaveHandler}
      onCloseModal={() => closeModal(false)}
      translations={translations}
    />
  );
};

export default InviteUser;
